import { useTheme } from "styled-components";

import { getRefreshBucketStart, HaNContext, useAreaConnections } from "@lib/logic";
import { type AreaWithSensorInfo, getFloorById, useAppSelector } from "@lib/store";
import { dateKeyForDateTimeString } from "@lib/store/src/data/timeslots/dateTimeHelpers";
import { useRequiredContext } from "@lib/utils";
import { AvatarStack, ModalCellItemIcon } from "@molecules";

import { AreaBusynessLabel } from "./components/AreaBusynessLabel";

/** An area in the list and the carousel: its floor, the connections working there now, and how busy its sensors say it is. */
export const AreaCell = ({ area }: { area: AreaWithSensorInfo }) => {
  const theme = useTheme();
  const { bucket, bucketWindow } = useRequiredContext(HaNContext);
  const floor = useAppSelector(state => getFloorById(state, area.floorId));

  // The refresh bucket's start, so "now" moves along every 5 minutes while the page is open, with the data. Bookings
  // are in the building's local time.
  const now = new Date(getRefreshBucketStart(bucket));
  const nowKey = bucketWindow.start;
  const date = dateKeyForDateTimeString(nowKey);
  const presentConnections = useAreaConnections({ areaId: area.id, date }).filter(({ reservation }) => {
    return nowKey >= reservation.localStart && nowKey <= reservation.localEnd;
  });

  return (
    <div className="flex w-full items-center justify-between gap-2">
      <div className="flex min-w-0 items-start gap-3">
        <ModalCellItemIcon
          className="flex-none self-start"
          backgroundColor={theme.card.beige500.background}
          icon="desk"
        />
        <div className="flex min-w-0 flex-col justify-center gap-0.5 pt-0.5">
          <p className="truncate font-semibold">{area.name}</p>
          {floor && <p className="truncate text-mapiq-black-500">{floor.name}</p>}

          <AvatarStack
            date={now.toISOString()}
            users={presentConnections.map(({ user }) => user)}
            overlap
          />
        </div>
      </div>

      <AreaBusynessLabel status={area.sensorInformation?.status ?? "unknown"} />
    </div>
  );
};
